import type { ReactNode } from 'react';
import { Icon, cn } from '@forecastify/ui';
import { useT, type MessageKey } from '../i18n';
import { EYEBROW } from '../chrome/tones';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Kívülről látod* — the boundary, beside every tab, never scrolled away.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **AN ASIDE THAT STAYS, WHERE THE FIRST VERSION WAS A PANEL AT THE BOTTOM OF ONE TAB.** The
 * redesign puts it to the right of every tab, sticky, so the question it answers — *"how many tasks
 * does this client have?"* — meets its answer wherever it is asked.
 *
 * ⚠️⚠️ **THE MOCKUP'S LIST, MINUS THE LINE S-16 REFUSED.** The redesign's *Látod* column includes
 * *"Tagok neve, e-mail-címe, állapota"* and its body says the operator manages *"a tagok
 * hozzáférését"* — the Users tab's claim, and the product owner ruled it out: `members` carries a
 * `workspace_id`, and the operator role holds no grant on any such table. What an operator DOES see
 * of a person is the contact, through `tenant_counts`; that line takes the refused one's place.
 *
 * ⚠️ **The last line names the ENFORCEMENT, not the policy.** A boundary somebody believes is a
 * convention is a boundary somebody asks to have lifted.
 */

const VISIBLE: readonly MessageKey[] = [
  'tenant.boundary.v1',
  'tenant.boundary.v2',
  'tenant.boundary.v3',
  'tenant.boundary.v4',
];

const HIDDEN: readonly MessageKey[] = [
  'tenant.boundary.h1',
  'tenant.boundary.h2',
  'tenant.boundary.h3',
  'tenant.boundary.h4',
];

export function BoundaryPanel(): ReactNode {
  const t = useT();

  return (
    <aside
      aria-label={t('tenant.boundary.title')}
      className="flex flex-col gap-3 self-start rounded-lg border p-4 xl:sticky xl:top-4"
    >
      <div className="flex items-center gap-2">
        <Icon name="shield-check" size={15} className="text-primary" />
        <h3 className="text-[13px] font-semibold">{t('tenant.boundary.title')}</h3>
      </div>
      <p className="text-[12.5px] leading-[1.55] text-pretty">{t('tenant.boundary.body')}</p>
      <div className="grid grid-cols-2 gap-3 border-t pt-3 xl:grid-cols-1">
        <div className="flex flex-col gap-1.5">
          <span className={cn('text-muted-foreground', EYEBROW)}>
            {t('tenant.boundary.visible')}
          </span>
          {VISIBLE.map((key) => (
            <span key={key} className="flex items-start gap-1.5 text-[12.5px]">
              <Icon
                name="check"
                size={13}
                className="mt-0.5 shrink-0 text-[var(--status-ok-text)]"
              />
              {t(key)}
            </span>
          ))}
        </div>
        <div className="flex flex-col gap-1.5">
          <span className={cn('text-muted-foreground', EYEBROW)}>
            {t('tenant.boundary.hidden')}
          </span>
          {HIDDEN.map((key) => (
            <span
              key={key}
              className="text-muted-foreground flex items-start gap-1.5 text-[12.5px]"
            >
              <Icon name="x" size={13} className="mt-0.5 shrink-0" />
              {t(key)}
            </span>
          ))}
        </div>
      </div>
      <p className="text-muted-foreground border-t pt-3 text-[11.5px] leading-[1.5] text-pretty">
        {t('tenant.boundary.enforced')}
      </p>
    </aside>
  );
}
